import React, { FC, ReactNode } from "react"
import { SpeedInsights } from "@vercel/speed-insights/react"
import { graphql, useStaticQuery } from "gatsby"

// Shared Components Library
import { SiteFooter, SiteNav } from "@helpscout/hs-marketing-shared-components"

// Shared Components
import SiteFooterSmall from "../site/SiteFooterSmall/SiteFooterSmall"

// Sub-components
import AnchorLink from "@components/AnchorLink/AnchorLink"
import Beacon from "@components/Beacon/Beacon"
import FixedCtaBar from "@components/FixedCtaBar/FixedCtaBar"
import HashHandler from "./components/HashHandler/HashHandler"
import QueryStringHandler from "./components/QueryStringHandler/QueryStringHandler"

// Consts
import {
  USE_SMALL_FOOTER_PATHS,
  HIDE_FOOTER_PATHS,
  INCLUDE_FIXED_CTA_BAR_PATHS_REGEX,
  USE_STATIC_HEADER_PATHS_REGEX,
} from "./Layout.constants"
import useCommonUrlsAndNumbers from "@hooks/useCommonUrlsAndNumbers"

// Types/interfaces
interface LayoutProps {
  children: ReactNode
  path: string
  template?: string
  useStaticHeader?: boolean
}

interface LayoutInternalVars {
  hideFooter: boolean
  includeFixedCtaBar: boolean
  isHomePage: boolean
  useSmallFooter: boolean
  useStaticHeader: boolean
}

// Component
const Layout: FC<LayoutProps> = ({
  children,
  path,
  useStaticHeader: _useStaticHeader,
}) => {
  const {
    hideFooter,
    includeFixedCtaBar,
    isHomePage,
    useSmallFooter,
    useStaticHeader,
  }: LayoutInternalVars = {
    hideFooter: HIDE_FOOTER_PATHS.includes(path),
    includeFixedCtaBar: !!path.match(INCLUDE_FIXED_CTA_BAR_PATHS_REGEX),
    isHomePage: path === "/",
    useSmallFooter: USE_SMALL_FOOTER_PATHS.includes(path),
    useStaticHeader: _useStaticHeader || !!path.match(USE_STATIC_HEADER_PATHS_REGEX),
  }

  const { demoUrl, signupPlusUrl, loginUrl } = useCommonUrlsAndNumbers()

  const { siteFooterData, siteNavData } = useStaticQuery<any>(graphql`
    query LayoutQuery {
      siteFooterData: contentfulJsonData(contentful_id: {eq: "4ywB5F9sHaNQzi0wnwHtix"}) {
        json {
          columns {
            title
            items {
              title
              url
              html
              isNew
            }
          }
        }
      }
      siteNavData: contentfulJsonData(contentful_id: {eq: "3YkxWPwJ7xkjNbrYfNNVTn"}) {
        json {
          navItems {
            title
            url
            navItems {
              title
              description
              icon
              url
            }
            feature {
              overline
              title
              imageId
              image {
                images {
                  fallback {
                    src
                    srcSet
                    sizes
                  }
                }
                layout
                width
                height
              }
              url
            }
          }
        }
      }
    }
  `)

  return (
    <>
      <HashHandler />
      <QueryStringHandler />
      <SiteNav
        LinkComponent={AnchorLink}
        demoUrl={demoUrl || ""}
        includeLoginButton={isHomePage}
        isStatic={useStaticHeader}
        loginUrl={loginUrl || ""}
        navItems={siteNavData.json.navItems}
        signupPlusUrl={signupPlusUrl || ""}
      />
      <main id="main-content">{children}</main>
      {!hideFooter && (useSmallFooter ? (
        <SiteFooterSmall />
      ) : (
        <SiteFooter
          LinkComponent={AnchorLink}
          columns={siteFooterData.json.columns}
        />
      ))}
      {includeFixedCtaBar && (
        <FixedCtaBar />
      )}
      <Beacon />
      <SpeedInsights />
      {(path === "/" || path === "/pricing/") && (
        <img
          src={`https://accretivemedia.go2cloud.org/aff_i?offer_id=420&aff_id=1&source=${
            // Pricing page
            path === "/pricing/" ? "ty" :
              // Home page
              "lp"
            }`}
          width="0"
          height="0"
          style={{
            position: "absolute",
            visibility: "hidden",
          }}
        />
      )}
    </>
  )
}

export default Layout
